import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { List, Map, OrderedMap } from 'immutable';
import { addChannelMembers } from 'flow/actions/channels-actions';
import { fetchExtensions } from 'flow/actions/extension-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { ChannelKey, ChatUserKey, ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { extractName, sortByName } from 'flow/utils';
import {
    filterExtensionsBySearchableInfo,
    getFixedNumberFromContact,
    getMobileNumberFromContact,
} from 'flow/utils/extension-utils';
import { twMerge } from 'tailwind-merge';
import FilterSearchBar from '../FilterSearchBar';
import Loading from 'flow/components/ui-components/Loading';
import SettingRow from '../Setting/SettingRow';
import BaseModal from '../ui-components/BaseModal';
import ContactAvatar from '../ui-components/ContactAvatar';
import Divider from '../ui-components/Divider';
import Icon from '../ui-components/Icon';
import IconButton from '../ui-components/IconButton';

interface Props {
    channelKey?: ChannelKey;
}

const AddLiveChatMemberModal = ({ channelKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const channelMembers = useAppSelector((state) =>
        state.channels.getIn(['channels', channelKey, 'channelMemberDTOs']),
    );
    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    const [filterText, setFilterText] = useState('');
    const [channelMemberList, setQueueMemberList] = useState(List());

    useEffect(() => {
        dispatch(fetchExtensions('contact'));
    }, [dispatch, channelKey]);

    if (!extensions || !channelMembers) {
        return <Loading />;
    }

    const currentChannelMemberKeys = channelMembers
        ? channelMembers.map((member) => member.get('chatUserKey')).toSet()
        : List();

    const filteredExtensions = extensions.filter(
        (extension) =>
            !currentChannelMemberKeys.contains(extension.get('chatUserKey')),
    );

    const unSelectedMembers = filteredExtensions.filter(
        (extension) =>
            !channelMemberList.contains(extension.get('chatUserKey')),
    );

    const addedMembers = channelMemberList.reduce((acc, key) => {
        const member = filteredExtensions
            .reduce((acc, extension) => {
                return acc.set(extension.get('chatUserKey'), extension);
            }, Map())
            .get(key) as TypedMap<Extension>;
        return acc.set(member.get('chatUserKey'), member);
    }, OrderedMap());

    const handleSelectClick = (chatUserKey: ChatUserKey) => {
        setQueueMemberList(channelMemberList.push(chatUserKey));
        setFilterText('');
    };

    const onSubmit = () => {
        const newExtensionKeys: ExtensionKey[] = [];
        extensions
            .filter((extension) =>
                channelMemberList.contains(extension.get('chatUserKey')),
            )
            .forEach((extension) => {
                newExtensionKeys.push(extension.get('key'));
            });

        if (newExtensionKeys.length) {
            dispatch(addChannelMembers(channelKey, newExtensionKeys));
        }
    };

    return (
        <BaseModal
            open={true}
            header={i18next.t('chatsettings.add_member')}
            confirmButtonText={i18next.t('chatsettings.add')}
            className="h-[39.5rem] w-[600px]"
            onSubmit={onSubmit}
        >
            <div className="flex flex-col gap-3">
                <FilterSearchBar
                    searchText={filterText}
                    placeholder={i18next.t('input.filter')}
                    handleFilter={(event, value) => {
                        setFilterText(value);
                    }}
                />

                {!addedMembers.isEmpty() ? (
                    <div className="flex flex-wrap gap-3">
                        {addedMembers
                            .map((extension: TypedMap<Extension>) => (
                                <div
                                    key={extension.get('chatUserKey')}
                                    className="flex items-center rounded-sm border border-gray-300 p-1"
                                >
                                    <ContactAvatar
                                        contact={extension.get('contact')}
                                        size="small"
                                        fallBackBackgroundColorClass={twMerge(
                                            'group-hover:bg-gray-300 bg-gray-200',
                                        )}
                                        lazyLoad={true}
                                    />
                                    <div className="ms-2 me-3 text-sm">
                                        {extension.getIn(
                                            ['contact', 'firstName'],
                                            '',
                                        ) ||
                                            getFixedNumberFromContact(
                                                extension.get('contact'),
                                            ) ||
                                            getMobileNumberFromContact(
                                                extension.get('contact'),
                                            ) ||
                                            null}
                                    </div>
                                    <div>
                                        <IconButton
                                            aria-label={i18next.t('remove')}
                                            className="size-6"
                                            onClick={() =>
                                                setQueueMemberList(
                                                    channelMemberList.filter(
                                                        (key) =>
                                                            key !==
                                                            extension.get(
                                                                'chatUserKey',
                                                            ),
                                                    ) as List<unknown>,
                                                )
                                            }
                                        >
                                            <Icon iconKey="cancel" />
                                        </IconButton>
                                    </div>
                                </div>
                            ))
                            .valueSeq()}
                    </div>
                ) : null}

                <Divider className="my-0" />

                {!unSelectedMembers.isEmpty()
                    ? filterExtensionsBySearchableInfo(
                          unSelectedMembers.sort(sortByName),
                          filterText,
                      )
                          .map((extension: TypedMap<Extension>) => (
                              <SettingRow
                                  key={extension.get('key')}
                                  className="h-12"
                                  cta={
                                      <IconButton
                                          aria-label={i18next.t(
                                              'chatsettings.add',
                                          )}
                                          className="reveal-on-hover-or-focus flex"
                                          onClick={() =>
                                              handleSelectClick(
                                                  extension.get('chatUserKey'),
                                              )
                                          }
                                      >
                                          <Icon iconKey="add" />
                                      </IconButton>
                                  }
                              >
                                  <ContactAvatar
                                      contact={extension.get('contact')}
                                      size="small"
                                      fallBackBackgroundColorClass={twMerge(
                                          'group-hover:bg-gray-300 bg-gray-200',
                                      )}
                                  />

                                  <div className="truncate text-gray-800">
                                      {extractName(
                                          extension.get('contact'),
                                          i18next.t('status.unknown'),
                                      )}
                                  </div>
                              </SettingRow>
                          ))
                          .valueSeq()
                    : null}
            </div>
        </BaseModal>
    );
};

export default AddLiveChatMemberModal;
